/**
 * Forgot Password Page
 * 
 * Page for users to request a password reset link.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Input, Card } from '../../components/ui';
import { authService } from '../../services';

type PageState = 'form' | 'success';

const ForgotPasswordPage = () => {
  const { t } = useTranslation();
  
  const [pageState, setPageState] = useState<PageState>('form');
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    
    if (!email.trim()) {
      setError(t('auth.forgotPassword.errors.emailRequired'));
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const response = await authService.requestPasswordReset(email);
      
      if (response.success) {
        setPageState('success');
      } else {
        setError(response.error?.message || t('auth.forgotPassword.errors.generic'));
      }
    } catch {
      setError(t('auth.forgotPassword.errors.generic'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (pageState === 'success') {
    return (
      <Card>
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">
            {t('auth.forgotPassword.success.title')}
          </h2>
          <p className="text-slate-500 mb-6">
            {t('auth.forgotPassword.success.message')}
          </p>
          <Link to="/auth/login">
            <Button variant="secondary" className="w-full">
              {t('auth.forgotPassword.backToLogin')}
            </Button>
          </Link>
        </div>
      </Card>
    );
  }

  // Form state
  return (
    <Card>
      <div className="text-center mb-6">
        <div className="w-16 h-16 mx-auto mb-4 bg-violet-100 rounded-full flex items-center justify-center">
          <svg className="w-8 h-8 text-violet-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-slate-900">
          {t('auth.forgotPassword.title')}
        </h1>
        <p className="text-slate-500 mt-2">
          {t('auth.forgotPassword.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <Input
          label={t('auth.email')}
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="email@example.com"
          required
          autoComplete="email"
          disabled={isSubmitting}
        />

        {error && (
          <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" isLoading={isSubmitting}>
          {t('auth.forgotPassword.submit')}
        </Button>
      </form>

      <div className="mt-6 text-center text-sm text-slate-500">
        <Link to="/auth/login" className="text-violet-600 hover:text-violet-700 font-medium">
          {t('auth.forgotPassword.backToLogin')}
        </Link>
      </div>
    </Card>
  );
};

export default ForgotPasswordPage;
